import { defineStore } from 'pinia';

export const AUTH_BANNER_STORAGE_KEY = 'qweebi:auth:banner';
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'qweebi:ui:sidebar-collapsed';
export type AuthBannerType = 'success' | 'warning';

export type AuthBannerData = {
	type: AuthBannerType;
	message: string;
} | null;

const readStoredSidebarCollapsed = (): boolean => {
	try {
		return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
};

const writeStoredSidebarCollapsed = (value: boolean): void => {
	try {
		localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, value.toString());
	} catch {
		// Storage can be unavailable (private mode, blocked cookies) - the preference is simply not persisted.
	}
};

export type UiControlState = {
	displayUseDesktopModal: boolean;
	authBanner: AuthBannerData;
	isShowingIntercomTopBanner: boolean;
	mobileSidebarOpen: boolean;
	sidebarCollapsed: boolean;
	sidebarCollapsedPreference: boolean;
	sidebarInitialized: boolean;
};

export const useUiControlStore = defineStore('ui-control', {
	state: (): UiControlState => {
		return {
			displayUseDesktopModal: false,
			authBanner: null,
			isShowingIntercomTopBanner: false,
			mobileSidebarOpen: false,
			sidebarCollapsed: false,
			sidebarCollapsedPreference: readStoredSidebarCollapsed(),
			sidebarInitialized: false,
		};
	},
	getters: {
		allClearToDisplayDashboard: (state) => {
			return !state.displayUseDesktopModal;
		},
	},
	actions: {
		updateIntercomTopBannerVisibility: function (visibility: boolean) {
			this.isShowingIntercomTopBanner = visibility;
		},
		openUseDesktopModal: function () {
			this.displayUseDesktopModal = true;
		},
		hideUseDesktopModal: function () {
			this.displayUseDesktopModal = false;
		},
		setAuthBanner: function (data: AuthBannerData) {
			this.authBanner = data;
		},
		clearAuthBanner: function () {
			this.authBanner = null;
		},
		toggleMobileSidebar: function () {
			this.mobileSidebarOpen = !this.mobileSidebarOpen;
		},
		closeMobileSidebar: function () {
			this.mobileSidebarOpen = false;
		},
		toggleSidebar: function () {
			this.sidebarCollapsed = !this.sidebarCollapsed;
		},
		setSidebarCollapsed: function (value: boolean) {
			this.sidebarCollapsed = value;
		},
		setSidebarCollapsedPreference: function (value: boolean) {
			this.sidebarCollapsedPreference = value;
			writeStoredSidebarCollapsed(value);
		},
	},
});

export type UiControlStore = ReturnType<typeof useUiControlStore>;
